import {
  INSTAGRAM as INSTAGRAM_CONSTANT,
  FACEBOOK as FACEBOOK_CONSTANT,
  TIKTOK as TIKTOK_CONSTANT,
  YOUTUBE as YOUTUBE_CONSTANT,
  INSTAGRAM_CONTENT_TYPE_CAROUSEL,
  INSTAGRAM_CONTENT_TYPE_IMAGE,
  INSTAGRAM_CONTENT_TYPE_STORIES,
  FACEBOOK_CONTENT_TYPE_CAROUSEL,
  FACEBOOK_CONTENT_TYPE_POST,
  FACEBOOK_CONTENT_TYPE_STORIES,
  TIKTOK_CONTENT_TYPE_CAROUSEL,
  TIKTOK_CONTENT_TYPE_IMAGE,
  MAX_CAROUSEL_IMAGES_INSTAGRAM,
  MAX_CAROUSEL_IMAGES_FACEBOOK,
  MAX_CAROUSEL_IMAGES_TIKTOK,
} from "../../../../utils/constants";

export const SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG = {
  [INSTAGRAM_CONSTANT]: {
    field: "platformsData.instagram.contentType",
    carouselValue: INSTAGRAM_CONTENT_TYPE_CAROUSEL,
    publicationValue: INSTAGRAM_CONTENT_TYPE_IMAGE,
    storyValue: INSTAGRAM_CONTENT_TYPE_STORIES,
  },
  [FACEBOOK_CONSTANT]: {
    field: "platformsData.facebook.contentType",
    carouselValue: FACEBOOK_CONTENT_TYPE_CAROUSEL,
    publicationValue: FACEBOOK_CONTENT_TYPE_POST,
    storyValue: FACEBOOK_CONTENT_TYPE_STORIES,
  },
  [TIKTOK_CONSTANT]: {
    field: "platformsData.tiktok.contentType",
    carouselValue: TIKTOK_CONTENT_TYPE_CAROUSEL,
    publicationValue: TIKTOK_CONTENT_TYPE_IMAGE,
    storyValue: null,
  },
};

const SHARE_MODAL_CAROUSEL_LIMITS_BY_PLATFORM = {
  [INSTAGRAM_CONSTANT]: MAX_CAROUSEL_IMAGES_INSTAGRAM,
  [FACEBOOK_CONSTANT]: MAX_CAROUSEL_IMAGES_FACEBOOK,
  [TIKTOK_CONSTANT]: MAX_CAROUSEL_IMAGES_TIKTOK,
};

export const getShareModalMaxCarouselImages = ({
  selectedPlatforms = [],
}) => {
  const limits = selectedPlatforms
    .map((platform) => SHARE_MODAL_CAROUSEL_LIMITS_BY_PLATFORM[platform])
    .filter(Boolean);

  if (limits.length === 0) {
    return MAX_CAROUSEL_IMAGES_INSTAGRAM;
  }

  return Math.min(...limits);
};

export const isGeneralDescriptionRequired = ({
  selectedPlatforms = [],
  platformsData = {},
}) => {
  if (selectedPlatforms.length === 0) {
    return true;
  }

  return selectedPlatforms.some((platform) => {
    if (platform === INSTAGRAM_CONSTANT || platform === FACEBOOK_CONSTANT) {
      const contentType = platformsData?.[platform]?.contentType;
      if (!contentType) {
        return true;
      }

      const category = getContentTypeCategory(
        contentType,
        platform,
        SHARE_MODAL_PLATFORM_CONTENT_TYPE_CONFIG
      );

      return category !== CONTENT_TYPE_CATEGORY.STORY;
    }

    if (platform === YOUTUBE_CONSTANT) {
      return false;
    }

    return true;
  });
};

export const CONTENT_TYPE_CATEGORY = {
  CAROUSEL: "CAROUSEL",
  PUBLICATION: "PUBLICATION",
  STORY: "STORY",
  OTHER: "OTHER",
};

export const detectInitialCommonState = (
  currentTypes,
  selectedPlatforms,
  platformConfig
) => {
  if (selectedPlatforms.length === 0) return false;

  return selectedPlatforms.every((platform) => {
    const config = platformConfig[platform];
    if (!config) return true;

    const currentType = currentTypes[platform];
    const publicationValue = config.publicationValue;

    return !currentType || currentType === publicationValue;
  });
};

export const getContentTypeCategory = (
  contentType,
  platform,
  platformConfig
) => {
  const config = platformConfig[platform];
  if (!config) return CONTENT_TYPE_CATEGORY.OTHER;

  if (contentType === config.carouselValue) {
    return CONTENT_TYPE_CATEGORY.CAROUSEL;
  }

  if (contentType === config.publicationValue) {
    return CONTENT_TYPE_CATEGORY.PUBLICATION;
  }

  if (contentType === config.storyValue) {
    return CONTENT_TYPE_CATEGORY.STORY;
  }

  return CONTENT_TYPE_CATEGORY.OTHER;
};

export const shouldSyncAllPlatforms = (category) => {
  return (
    category === CONTENT_TYPE_CATEGORY.CAROUSEL ||
    category === CONTENT_TYPE_CATEGORY.PUBLICATION
  );
};

export const getPlatformsToSync = (
  category,
  selectedPlatforms,
  platformConfig
) => {
  if (category === CONTENT_TYPE_CATEGORY.CAROUSEL) {
    return selectedPlatforms.filter((p) => platformConfig[p]);
  }

  if (category === CONTENT_TYPE_CATEGORY.PUBLICATION) {
    return selectedPlatforms.filter((p) => platformConfig[p]);
  }

  if (category === CONTENT_TYPE_CATEGORY.STORY) {
    return selectedPlatforms.filter(
      (p) =>
        platformConfig[p] &&
        (p === INSTAGRAM_CONSTANT || p === FACEBOOK_CONSTANT)
    );
  }

  return [];
};

export const mapContentTypeForPlatform = (
  contentType,
  sourcePlatform,
  targetPlatform,
  platformConfig
) => {
  const sourceConfig = platformConfig[sourcePlatform];
  const targetConfig = platformConfig[targetPlatform];

  if (!sourceConfig || !targetConfig) return contentType;

  const category = getContentTypeCategory(
    contentType,
    sourcePlatform,
    platformConfig
  );

  if (category === CONTENT_TYPE_CATEGORY.CAROUSEL) {
    return targetConfig.carouselValue;
  }

  if (category === CONTENT_TYPE_CATEGORY.PUBLICATION) {
    return targetConfig.publicationValue;
  }

  if (category === CONTENT_TYPE_CATEGORY.STORY) {
    if (targetPlatform === TIKTOK_CONSTANT) {
      return targetConfig.publicationValue;
    }
    return targetConfig.storyValue;
  }

  return contentType;
};

export const getEquivalentContentType = (
  contentType,
  sourcePlatform,
  targetPlatform,
  platformConfig
) => {
  return mapContentTypeForPlatform(
    contentType,
    sourcePlatform,
    targetPlatform,
    platformConfig
  );
};
